import { Link, useSearchParams } from '@remix-run/react'
import { motion } from 'framer-motion'
import { Briefcase, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '#app/components/ui/button.tsx'
import { getWorkUrl } from '#app/utils/navigation.ts'

interface StickyNavigationProps {
	currentSlug: string
	allSlugs: string[]
}

export function StickyNavigation({
	currentSlug,
	allSlugs,
}: StickyNavigationProps) {
	const [searchParams] = useSearchParams()

	const currentIndex = allSlugs.findIndex((slug) => slug === currentSlug)
	const previousSlug = currentIndex > 0 ? allSlugs[currentIndex - 1] : null
	const nextSlug =
		currentIndex < allSlugs.length - 1 ? allSlugs[currentIndex + 1] : null

	return (
		<motion.nav
			initial={{ y: -100, opacity: 0 }}
			animate={{ y: 0, opacity: 1 }}
			transition={{ duration: 0.3 }}
			className="mx-auto w-full min-w-0 max-w-7xl justify-between lg:flex"
			role="navigation"
			aria-label="Work experience navigation"
		>
			<div className="container flex w-full items-center justify-between gap-4 px-2 py-3 text-black contrast-more:text-white dark:text-white">
				{/* Back to All Experiences */}
				<Button
					asChild
					variant="ghost"
					size="sm"
					className="flex items-center gap-2 text-black contrast-more:text-white dark:text-white"
				>
					<Link
						to={getWorkUrl(searchParams)}
						aria-label="Back to all work experiences"
					>
						<Briefcase className="h-4 w-4" aria-hidden="true" />
						<span className="hidden sm:inline">All Experiences</span>
					</Link>
				</Button>

				{/* Position indicator */}
				<span className="text-sm text-black dark:text-white" aria-live="polite">
					{currentIndex >= 0 ? currentIndex + 1 : 0}/{allSlugs.length}
				</span>

				{/* Previous/Next Navigation */}
				<div className="flex items-center gap-2">
					<Button
						asChild={!!previousSlug}
						variant="ghost"
						size="sm"
						disabled={!previousSlug}
						className="flex items-center gap-2 text-black contrast-more:text-white dark:text-white"
					>
						{previousSlug ? (
							<Link
								to={`/work/${previousSlug}`}
								aria-label="Previous work experience"
							>
								<ChevronLeft className="h-4 w-4" aria-hidden="true" />
								<span className="hidden sm:inline">Previous</span>
							</Link>
						) : (
							<>
								<ChevronLeft className="h-4 w-4" aria-hidden="true" />
								<span className="hidden sm:inline">Previous</span>
							</>
						)}
					</Button>
					<Button
						asChild={!!nextSlug}
						variant="ghost"
						size="sm"
						disabled={!nextSlug}
						className="flex items-center gap-2 text-black contrast-more:text-white dark:text-white"
					>
						{nextSlug ? (
							<Link to={`/work/${nextSlug}`} aria-label="Next work experience">
								<span className="hidden sm:inline">Next</span>
								<ChevronRight className="h-4 w-4" aria-hidden="true" />
							</Link>
						) : (
							<>
								<span className="hidden sm:inline">Next</span>
								<ChevronRight className="h-4 w-4" aria-hidden="true" />
							</>
						)}
					</Button>
				</div>
			</div>
		</motion.nav>
	)
}
